import { Smartphone } from 'lucide-react'
import { useLocation } from 'react-router-dom'

import { useEffect, useState } from 'react'

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, Input, PosButton } from '@/components/ui'
import { CameraScanButton } from '@/components/pos/scan/CameraScanButton'
import { useRegisterContext } from '@/contexts/RegisterContext'
import { useSession } from '@/contexts/SessionContext'
import { cn } from '@/lib/utils'
import { companionMaster, PAIR_PERMISSION, useCompanionMaster } from '@/services/companion/master'

/** In the layout: the screen the companion's scans land on, and the end of the link on shift close, lock or sign-out. */
export const CompanionWatch = () => {
  const register = useRegisterContext()
  const location = useLocation()
  useEffect(() => companionMaster.setScreen(location.pathname === '/' ? 'sell' : 'other'), [location.pathname])
  useEffect(() => {
    if (!register.opened_at) companionMaster.stop('shift_closed')
  }, [register.opened_at])
  useEffect(() => () => companionMaster.stop('signed_out'), [])
  return null
}

/** Badge in the register header and the pairing dialog. */
export const CompanionControl = () => {
  const state = useCompanionMaster()
  const { can } = useSession()
  const register = useRegisterContext()
  const [open, setOpen] = useState(false)
  const [code, setCode] = useState('')

  useEffect(() => {
    if (state.kind === 'asking') setOpen(true)
  }, [state.kind])

  if (state.kind === 'off' && !can(PAIR_PERMISSION)) return null
  const dot = state.kind === 'linked' ? (state.online ? 'bg-success' : 'bg-grey-500') : state.kind === 'off' ? null : 'bg-warning'
  const connect = (text = code) =>
    companionMaster.connect(text, {
      terminal_no: register.terminal_no ?? 0,
      terminal_name: register.name,
      branch: register.branch ?? '',
      staff: register.cashier,
    })

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 rounded-full border border-border px-2 py-1 font-medium text-ink sm:px-3"
        title="Companion"
      >
        <Smartphone className="size-3.5" />
        {dot && <span className={cn('size-2 rounded-full', dot)} />}
        <span className="hidden sm:inline">Companion</span>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Companion</DialogTitle>
            <DialogDescription>A phone with the POS app scans into this device and shows the cart to the customer, until the shift closes.</DialogDescription>
          </DialogHeader>
          {state.kind === 'off' && (
            <div className="grid gap-3">
              <p className="text-sm text-muted-foreground">
                On the phone, open the POS app → Use as a companion → Let a PC connect, then type its code here. Chrome asks to connect to devices
                on your network the first time: choose Allow.
              </p>
              <div className="flex items-center gap-2">
                <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="00BG-JTMJ" className="font-mono" />
                <CameraScanButton
                  onScan={(text) => {
                    setCode(text)
                    connect(text)
                  }}
                />
              </div>
              {state.error && <p className="text-sm text-error">{state.error}</p>}
              <PosButton size="touch" disabled={code.replace(/[^0-9A-Z]/gi, '').length < 8} onClick={() => connect()}>
                Pair companion
              </PosButton>
            </div>
          )}
          {state.kind === 'connecting' && <p className="text-sm text-muted-foreground">Connecting…</p>}
          {state.kind === 'asking' && (
            <div className="grid gap-3">
              <p className="font-medium text-ink">{state.device} wants to join</p>
              <p className="text-sm text-muted-foreground">It will scan into this device and see the cart. Allow only a device you recognise.</p>
              <div className="grid grid-cols-2 gap-2">
                <PosButton variant="secondary" size="touch" onClick={() => companionMaster.deny()}>
                  Deny
                </PosButton>
                <PosButton size="touch" onClick={() => companionMaster.allow()}>
                  Allow
                </PosButton>
              </div>
            </div>
          )}
          {state.kind === 'linked' && (
            <div className="grid gap-3">
              <p className="flex items-center gap-2 font-medium text-ink">
                <span className={cn('size-2 rounded-full', state.online ? 'bg-success' : 'bg-grey-500')} />
                {state.device} · {state.online ? 'connected' : 'disconnected'}
              </p>
              <PosButton variant="destructive" onClick={() => companionMaster.stop()}>
                Unpair
              </PosButton>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
